import React, { useMemo, useState } from "react";
import {
  Box, Chip, IconButton, InputAdornment, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Tooltip, Typography, alpha,
  MenuItem,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import BoltIcon from "@mui/icons-material/Bolt";
import {
  useGetDevicesQuery, useForceRefreshDeviceMutation,
} from "../../../Api/attoDeskApi";
import { IDevice } from "../../../Api/Interface/api.interface";
import {
  playerKind, playerDetail, screenLabel,
} from "../../Device/deviceDisplay";
import { useAppSelector } from "../../../Store/hooks";
import { selectIsSuperAdmin } from "../../../Store/Auth/AuthSelector";
import PageLoader from "../../../Components/Loader/PageLoader";
import { useNotifier } from "../../../Core/Notifier";
import {
  NAVY, GOLD, BORDER, TEXT_PRIMARY, TEXT_SECONDARY,
} from "../../../theme/tokens";

const POLL_MS = 10_000;

const formatBytes = (b?: number | null): string => {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1_048_576) return `${(b / 1024).toFixed(0)} KB`;
  if (b < 1_073_741_824) return `${(b / 1_048_576).toFixed(1)} MB`;
  return `${(b / 1_073_741_824).toFixed(2)} GB`;
};

const formatLastSeen = (iso?: string | null): string => {
  if (!iso) return "Never";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

/**
 * Derive the badge label for a device. We keep this slightly richer
 * than the IDevice.status enum so the dashboard can surface
 * authentication-failure and waiting-for-content states explicitly.
 */
type LiveStatus =
  | "online" | "offline" | "disabled" | "auth_failed" | "waiting";

const deriveStatus = (d: IDevice): LiveStatus => {
  if (d.is_disabled) return "disabled";
  if ((d.last_error || "").toLowerCase().includes("401")) return "auth_failed";
  if (!d.playlist_id && !d.playlist_group_id) return "waiting";
  if (d.status === "offline") return "offline";
  return "online";
};

const STATUS_STYLE: Record<LiveStatus, { label: string; bg: string; fg: string; dot: string }> = {
  online:      { label: "Online",                  bg: "#ecfdf5", fg: "#047857", dot: "#10b981" },
  offline:     { label: "Offline",                 bg: "#fef2f2", fg: "#b91c1c", dot: "#ef4444" },
  disabled:    { label: "Disabled",                bg: "#f1f5f9", fg: "#475569", dot: "#94a3b8" },
  auth_failed: { label: "Authentication Failed",   bg: "#fef3c7", fg: "#92400e", dot: "#f59e0b" },
  waiting:     { label: "Waiting for Content",     bg: "#e0e7ff", fg: "#3730a3", dot: "#6366f1" },
};

const StatusPill: React.FC<{ status: LiveStatus }> = ({ status }) => {
  const s = STATUS_STYLE[status];
  return (
    <Chip
      size="small"
      icon={<FiberManualRecordIcon sx={{ fontSize: 9 }} />}
      label={s.label}
      sx={{
        height: 22, fontSize: 11, fontWeight: 700, letterSpacing: "0.02em",
        bgcolor: s.bg, color: s.fg, border: `1px solid ${alpha(s.dot, 0.45)}`,
        "& .MuiChip-icon": { color: s.dot, ml: 0.75 },
      }}
    />
  );
};

const PlayerTypeChip: React.FC<{ type?: string | null }> = ({ type }) => {
  if (!type) return <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY }}>—</Typography>;
  const isAndroid = type.toLowerCase().startsWith("android");
  return (
    <Chip size="small" label={type.toUpperCase()}
      sx={{
        height: 18, fontSize: 10, fontWeight: 700,
        bgcolor: isAndroid ? "#dcfce7" : "#dbeafe",
        color: isAndroid ? "#166534" : "#1e40af",
      }} />
  );
};

const StorageBar: React.FC<{ used?: number | null; total?: number | null }> = ({ used, total }) => {
  if (!total || used == null) return <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY }}>—</Typography>;
  const pct = Math.min(100, Math.round((used / total) * 100));
  const color = pct > 90 ? "#dc2626" : pct > 75 ? "#f59e0b" : "#10b981";
  return (
    <Box sx={{ width: 120 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
        <Typography sx={{ fontSize: 10, color: TEXT_SECONDARY, fontWeight: 600 }}>{pct}%</Typography>
        <Typography sx={{ fontSize: 10, color: TEXT_SECONDARY }}>
          {formatBytes(used)} / {formatBytes(total)}
        </Typography>
      </Box>
      <Box sx={{ height: 4, borderRadius: 2, bgcolor: "#f1f5f9", overflow: "hidden" }}>
        <Box sx={{ width: `${pct}%`, height: "100%", bgcolor: color, transition: "width 0.3s ease" }} />
      </Box>
    </Box>
  );
};

/** "Aug 3, 2026, 10:42 AM" in the administrator's local timezone. */
const formatAbsolute = (iso?: string | null): string => {
  if (!iso) return "Not reported";
  return new Date(iso).toLocaleString(undefined, {
    month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
  });
};

/** "2 hours ago" — relative helper for the Last App Updated sub-line. */
const relativeAgo = (iso?: string | null): string => {
  if (!iso) return "";
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} minute${mins === 1 ? "" : "s"} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? "" : "s"} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
};

type AppUpdateStatus =
  | "up_to_date" | "update_available" | "update_pending" | "update_failed" | "not_reported";

const APP_UPDATE_STYLE: Record<AppUpdateStatus, { label: string; bg: string; fg: string }> = {
  up_to_date:       { label: "Up to date",       bg: "#ecfdf5", fg: "#047857" },
  update_available: { label: "Update available", bg: "#fef3c7", fg: "#92400e" },
  update_pending:   { label: "Update pending",   bg: "#dbeafe", fg: "#1e40af" },
  update_failed:    { label: "Update failed",    bg: "#fef2f2", fg: "#b91c1c" },
  not_reported:     { label: "Not reported",     bg: "#f1f5f9", fg: "#64748b" },
};

const normalizeAppUpdate = (s?: string | null): AppUpdateStatus =>
  (s && (s in APP_UPDATE_STYLE) ? s : "not_reported") as AppUpdateStatus;

const UpdateBadge: React.FC<{ status?: string | null }> = ({ status }) => {
  const s = APP_UPDATE_STYLE[normalizeAppUpdate(status)];
  return (
    <Chip size="small" label={s.label}
      sx={{ height: 18, fontSize: 10, fontWeight: 700, bgcolor: s.bg, color: s.fg }} />
  );
};

const MonitoringDevices: React.FC = () => {
  const navigate = useNavigate();
  const isSuper = useAppSelector(selectIsSuperAdmin);
  const { showMessage, showErrorMessage } = useNotifier();

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<LiveStatus | "all">("all");
  const [playerTypeFilter, setPlayerTypeFilter] = useState<"all" | "web" | "android">("all");
  const [appUpdateFilter, setAppUpdateFilter] = useState<AppUpdateStatus | "all">("all");
  const [sortKey, setSortKey] = useState<"version" | "app_updated" | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const toggleSort = (key: "version" | "app_updated") => {
    if (sortKey === key) { setSortDir((d) => (d === "asc" ? "desc" : "asc")); }
    else { setSortKey(key); setSortDir(key === "app_updated" ? "desc" : "asc"); }
  };

  const { data, isLoading, refetch } = useGetDevicesQuery(undefined, {
    pollingInterval: POLL_MS,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const [forceRefresh, { isLoading: isForcing }] = useForceRefreshDeviceMutation();

  const devices: IDevice[] = useMemo(
    () => (Array.isArray(data?.data) ? (data?.data as unknown as IDevice[]) : []),
    [data],
  );

  const filtered = useMemo(() => {
    let rows = devices;
    if (search) {
      const q = search.toLowerCase();
      rows = rows.filter(d =>
        d.device_code.toLowerCase().includes(q)
        || d.device_name.toLowerCase().includes(q)
        || (d.location ?? "").toLowerCase().includes(q),
      );
    }
    if (statusFilter !== "all") {
      rows = rows.filter(d => deriveStatus(d) === statusFilter);
    }
    if (playerTypeFilter !== "all") {
      rows = rows.filter(d => playerKind(d) === playerTypeFilter);
    }
    if (appUpdateFilter !== "all") {
      rows = rows.filter(d => normalizeAppUpdate(d.app_update_status) === appUpdateFilter);
    }
    if (sortKey) {
      const dir = sortDir === "asc" ? 1 : -1;
      rows = [...rows].sort((a, b) => {
        if (sortKey === "version") {
          // Reported-version first; unreported sinks to the bottom.
          return (a.app_version || "").localeCompare(b.app_version || "", undefined, { numeric: true }) * dir;
        }
        const ta = a.app_updated_at ? new Date(a.app_updated_at).getTime() : 0;
        const tb = b.app_updated_at ? new Date(b.app_updated_at).getTime() : 0;
        return (ta - tb) * dir;
      });
    }
    return rows;
  }, [devices, search, statusFilter, playerTypeFilter, appUpdateFilter, sortKey, sortDir]);

  const counts = useMemo(() => {
    const c = { online: 0, offline: 0, disabled: 0, auth_failed: 0, waiting: 0 };
    devices.forEach(d => { c[deriveStatus(d)]++; });
    return c;
  }, [devices]);

  const handleForceRefresh = async (id: number) => {
    try {
      const res = await forceRefresh(id).unwrap();
      if (res.status) { showMessage("Device will refresh on next poll"); refetch(); }
    } catch (e: any) { showErrorMessage(e?.data?.message || "Failed"); }
  };

  if (isLoading) return <PageLoader height="20vh" />;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: 1.5,
          display: "flex", alignItems: "center", justifyContent: "center",
          background: `linear-gradient(135deg, ${alpha("#0e7c9b", 0.18)} 0%, ${alpha("#0e7c9b", 0.06)} 100%)`,
          color: "#0e7c9b",
        }}>
          <MonitorHeartIcon />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography fontSize={20} fontWeight={800} color={NAVY} letterSpacing="-0.02em">
            Device Monitoring
          </Typography>
          <Typography fontSize={12} color={TEXT_SECONDARY}>
            Live status across {devices.length} device{devices.length === 1 ? "" : "s"} · auto-refresh every {POLL_MS / 1000}s
          </Typography>
        </Box>
        <IconButton onClick={() => refetch()}
          sx={{
            border: `1px solid ${BORDER}`, borderRadius: 2, width: 38, height: 38, bgcolor: "#fff",
            "&:hover": { borderColor: GOLD, bgcolor: alpha(GOLD, 0.06) },
          }}>
          <RefreshIcon sx={{ fontSize: 18, color: NAVY }} />
        </IconButton>
      </Box>

      {/* Summary tiles */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 2, mb: 3 }}>
        {([
          ["Online", counts.online, "#10b981", "online"],
          ["Offline", counts.offline, "#ef4444", "offline"],
          ["Disabled", counts.disabled, "#94a3b8", "disabled"],
          ["Auth Failed", counts.auth_failed, "#f59e0b", "auth_failed"],
          ["Waiting", counts.waiting, "#6366f1", "waiting"],
        ] as Array<[string, number, string, LiveStatus]>).map(([label, n, color, key]) => (
          <Paper key={label} elevation={0}
            onClick={() => setStatusFilter(statusFilter === key ? "all" : key)}
            sx={{
              p: 2, borderRadius: 3, cursor: "pointer",
              border: `1px solid ${statusFilter === key ? color : BORDER}`,
              bgcolor: statusFilter === key ? alpha(color, 0.06) : "#fff",
              transition: "all 0.15s ease",
              "&:hover": { borderColor: color, transform: "translateY(-1px)" },
            }}>
            <Typography sx={{ fontSize: 10.5, fontWeight: 700, textTransform: "uppercase",
              letterSpacing: "0.06em", color: TEXT_SECONDARY }}>{label}</Typography>
            <Typography sx={{ fontSize: 24, fontWeight: 800, color, mt: 0.5 }}>{n}</Typography>
          </Paper>
        ))}
      </Box>

      {/* Toolbar */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${BORDER}`, mb: 2 }}>
        <Box sx={{ display: "flex", gap: 1.5, p: 2, alignItems: "center" }}>
          <TextField size="small" placeholder="Search by code / name / location"
            value={search} onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: "#94a3b8" }} /></InputAdornment>,
            }}
            sx={{ width: 360, "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13, bgcolor: "#fff" } }} />
          <TextField select size="small" value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            sx={{ width: 200, "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13, bgcolor: "#fff" } }}>
            <MenuItem value="all">All statuses</MenuItem>
            <MenuItem value="online">Online</MenuItem>
            <MenuItem value="offline">Offline</MenuItem>
            <MenuItem value="disabled">Disabled</MenuItem>
            <MenuItem value="auth_failed">Authentication Failed</MenuItem>
            <MenuItem value="waiting">Waiting for Content</MenuItem>
          </TextField>
          <TextField select size="small" value={playerTypeFilter}
            onChange={(e) => setPlayerTypeFilter(e.target.value as any)}
            sx={{ width: 160, "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13, bgcolor: "#fff" } }}>
            <MenuItem value="all">All players</MenuItem>
            <MenuItem value="web">Web</MenuItem>
            <MenuItem value="android">Android</MenuItem>
          </TextField>
          <TextField select size="small" value={appUpdateFilter}
            onChange={(e) => setAppUpdateFilter(e.target.value as any)}
            sx={{ width: 200, "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13, bgcolor: "#fff" } }}>
            <MenuItem value="all">All app versions</MenuItem>
            <MenuItem value="up_to_date">Up to date</MenuItem>
            <MenuItem value="update_available">Update available</MenuItem>
            <MenuItem value="update_pending">Update pending</MenuItem>
            <MenuItem value="update_failed">Update failed</MenuItem>
            <MenuItem value="not_reported">Version not reported</MenuItem>
          </TextField>
        </Box>
      </Paper>

      <Paper elevation={0} sx={{ borderRadius: 3, border: `1px solid ${BORDER}`, bgcolor: "#fff" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {[
                  { label: "Device" }, { label: "Company" }, { label: "Location" }, { label: "Player" },
                  { label: "Status" }, { label: "Last Seen" }, { label: "Playlist Group" },
                  { label: "Version", sort: "version" as const },
                  { label: "Last App Updated", sort: "app_updated" as const },
                  { label: "Storage" }, { label: "Current Media" }, { label: "Last Sync" },
                  { label: "Actions", align: "right" as const },
                ].map((h) => {
                  const active = h.sort && sortKey === h.sort;
                  return (
                    <TableCell key={h.label} align={h.align ?? "left"}
                      onClick={h.sort ? () => toggleSort(h.sort!) : undefined}
                      sx={{
                        fontSize: 11, fontWeight: 700,
                        color: active ? NAVY : TEXT_SECONDARY,
                        textTransform: "uppercase", letterSpacing: "0.05em",
                        cursor: h.sort ? "pointer" : "default",
                        whiteSpace: "nowrap",
                        userSelect: "none",
                      }}>
                      {h.label}
                      {h.sort && (
                        <Box component="span" sx={{ ml: 0.5, color: active ? GOLD : alpha(TEXT_SECONDARY, 0.5) }}>
                          {active ? (sortDir === "asc" ? "▲" : "▼") : "⇅"}
                        </Box>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={13} sx={{ textAlign: "center", py: 6, color: TEXT_SECONDARY }}>
                    No devices match this filter.
                  </TableCell>
                </TableRow>
              ) : filtered.map((d) => {
                const status = deriveStatus(d);
                return (
                  <TableRow key={d.id} hover sx={{ cursor: "pointer" }}
                    onClick={() => navigate(`/devices/${d.id}/monitoring`)}>
                    <TableCell>
                      <Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY }}>
                          {d.device_code}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: TEXT_SECONDARY }}>
                          {d.device_name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, color: TEXT_PRIMARY }}>
                      {isSuper ? (d.company?.name || "—") : <span style={{ color: TEXT_SECONDARY }}>—</span>}
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, color: TEXT_PRIMARY }}>{d.location || "—"}</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                        <PlayerTypeChip type={d.player_type} />
                        {playerDetail(d) && (
                          <Typography sx={{ fontSize: 10.5, color: TEXT_SECONDARY, lineHeight: 1.3 }}>
                            {playerDetail(d)}
                          </Typography>
                        )}
                        {screenLabel(d) && (
                          <Typography sx={{ fontSize: 10.5, color: TEXT_SECONDARY, lineHeight: 1.3 }}>
                            {screenLabel(d)}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell><StatusPill status={status} /></TableCell>
                    <TableCell sx={{ fontSize: 12, color: TEXT_PRIMARY, whiteSpace: "nowrap" }}>
                      <Tooltip title={d.last_seen_at || ""}>
                        <span>{formatLastSeen(d.last_seen_at)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, color: TEXT_PRIMARY }}>
                      {d.playlist_group?.name || d.playlist?.name || "—"}
                    </TableCell>
                    {/* VERSION = the app version the device currently reports
                        (never the latest available release). */}
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Tooltip title="The app version currently installed on this device.">
                        <Box>
                          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: d.app_version ? NAVY : TEXT_SECONDARY }}>
                            {d.app_version || "Not reported"}
                          </Typography>
                          <Box sx={{ mt: 0.25 }}><UpdateBadge status={d.app_update_status} /></Box>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    {/* LAST APP UPDATED = when the app was installed/last updated
                        on this device (not last seen / last sync). */}
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {d.app_updated_at ? (
                        <Tooltip title={new Date(d.app_updated_at).toString()}>
                          <Box>
                            <Typography sx={{ fontSize: 12, color: TEXT_PRIMARY }}>
                              {formatAbsolute(d.app_updated_at)}
                            </Typography>
                            <Typography sx={{ fontSize: 10.5, color: TEXT_SECONDARY }}>
                              {relativeAgo(d.app_updated_at)}
                            </Typography>
                          </Box>
                        </Tooltip>
                      ) : (
                        <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY }}>Not reported</Typography>
                      )}
                    </TableCell>
                    <TableCell><StorageBar used={d.storage_used} total={d.storage_total} /></TableCell>
                    <TableCell sx={{ fontSize: 12, color: TEXT_PRIMARY, maxWidth: 200 }}>
                      <Typography noWrap sx={{ fontSize: 12 }}>
                        {d.current_media_name || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: 12, color: TEXT_SECONDARY, whiteSpace: "nowrap" }}>
                      {formatLastSeen(d.last_sync_at)}
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Box sx={{ display: "inline-flex", gap: 0.5 }}>
                        <Tooltip title="Force Refresh">
                          <IconButton size="small" disabled={isForcing || d.is_disabled}
                            onClick={() => handleForceRefresh(d.id)}
                            sx={{
                              border: `1px solid ${BORDER}`, borderRadius: 1.5, width: 30, height: 30,
                              color: "#f59e0b",
                              "&:hover": { bgcolor: "#fef3c7", borderColor: "#fcd34d" },
                            }}>
                            <BoltIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Open monitoring">
                          <IconButton size="small"
                            onClick={() => navigate(`/devices/${d.id}/monitoring`)}
                            sx={{
                              border: `1px solid ${BORDER}`, borderRadius: 1.5, width: 30, height: 30,
                              color: "#3b82f6",
                              "&:hover": { bgcolor: "#dbeafe", borderColor: "#bfdbfe" },
                            }}>
                            <OpenInNewIcon sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default MonitoringDevices;
